import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Autocomplete from '@material-ui/lab/Autocomplete'
import AutocompleteChips from 'ui/components/autocomplete/autocompleteChips'
import InputLabel from '@material-ui/core/InputLabel'
import TextField from '@material-ui/core/TextField'
import RadioGroup from '@material-ui/core/RadioGroup'
import Radio from '@material-ui/core/Radio'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Collapse from '@material-ui/core/Collapse'
import Typography from '@material-ui/core/Typography'
import useTranslation from 'hooks/useTranslations'
import SvgIcon from '@material-ui/core/SvgIcon'
import { ReactComponent as IconSprite } from 'assets/icons/sprite.svg'
import { ReactComponent as IconClose } from 'assets/icons/close.svg'
import { ReactComponent as IconArrowDown } from 'assets/icons/arrow-down.svg'
import useStyles from './inputStyles'

const AdvancedSearch = props => {
  const {
    vendorTypesOptions,
    vendorTypes,
    setVendorTypes,
    countries,
    country,
    setCountry,
    provincesOptions,
    provinces,
    setProvinces,
    searchNetwork,
    setSearchNetwork,
    documentsAndCertificatesOptions,
    documentsAndCertificates,
    setDocumentsAndCertificates,
    invalidDocuments,
    setInvalidDocuments,
    onSearch,
    expanded,
    setExpanded,
  } = props

  const classes = useStyles()
  const t = useTranslation()

  const handleExpandClick = () => {
    setExpanded(expanded => !expanded)
  }

  const handleCountryChange = (_, value) => {
    setCountry(value)
    setProvinces([])
  }

  const handleInvalidDocumentsChange = (_, value) => {
    setInvalidDocuments(value)
  }

  const handleSearchNetworkChange = (_, value) => {
    setSearchNetwork(value)
  }

  const handleSubmit = e => {
    e.preventDefault()
    onSearch()
  }

  return (
    <>
      <Button
        color="primary"
        className={expanded ? 'isActive' : ''}
        onClick={handleExpandClick}
        aria-expanded={expanded}
        startIcon={
          expanded ? (
            <SvgIcon component={IconClose} />
          ) : (
            <SvgIcon component={IconSprite} />
          )
        }
      >
        {expanded ? (
          <Box>{t('search:advanced-search')}</Box>
        ) : (
          <Box display={{ xs: 'none', sm: 'block' }}>
            {t('search:advanced-search')}
          </Box>
        )}
      </Button>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box mb={2} mt={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2">{t('search:search-network')}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Box mb={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                <RadioGroup
                  aria-label="searchNetwork"
                  name="searchNetwork"
                  value={searchNetwork}
                  onChange={handleSearchNetworkChange}
                >
                  <FormControlLabel
                    value="0"
                    control={<Radio />}
                    label={t('search:search-network:all')}
                  />
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label={t('search:search-network:in-my-network')}
                  />
                  <FormControlLabel
                    value="2"
                    control={<Radio />}
                    label={t('search:search-network:other')}
                  />
                </RadioGroup>
              </form>
            </Grid>
          </Grid>
        </Box>

        {vendorTypesOptions && vendorTypesOptions.length ? (
          <>
            <Box mb={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h2">
                    {t('search:vendor-types')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Grid container>
                <Grid item xs={12}>
                  <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                    <InputLabel htmlFor="vendor-types" shrink>
                      {t('search:vendor-types')}
                    </InputLabel>
                    <AutocompleteChips
                      id="vendor-types"
                      className={`${vendorTypes ? classes.hasValue : ''}`}
                      options={vendorTypesOptions}
                      placeholder={t('search:select-vendor-types')}
                      value={vendorTypes}
                      setValue={setVendorTypes}
                      noOptionsText={t('search:no-options-vendor-types')}
                    />
                  </form>
                </Grid>
              </Grid>
            </Box>
          </>
        ) : null}

        <Box mb={2} mt={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2">
                {t('search:scope-of-action')}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box mb={4}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                <InputLabel htmlFor="countries" shrink>
                  {t('vendors:country')}
                </InputLabel>
                <Autocomplete
                  id="countries"
                  className={`${country ? classes.hasValue : ''}`}
                  value={country}
                  fullWidth
                  onChange={handleCountryChange}
                  getOptionLabel={option => option.name || ''}
                  getOptionSelected={(option, value) =>
                    option.name === value.name
                  }
                  popupIcon={<SvgIcon component={IconArrowDown} />}
                  options={countries}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={t('vendors:country-placeholder')}
                    />
                  )}
                />
              </form>
            </Grid>
            {country && country.code === 'ES' ? (
              <Grid item xs={12} sm={6} md={4}>
                <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                  <InputLabel htmlFor="provinces" shrink>
                    {t('search:provinces')}
                  </InputLabel>
                  <AutocompleteChips
                    id="provinces"
                    className={`${provinces ? classes.hasValue : ''}`}
                    options={provincesOptions}
                    placeholder={t('search:provinces')}
                    value={provinces}
                    setValue={setProvinces}
                    noOptionsText={t('search:no-options-provinces')}
                  />
                </form>
              </Grid>
            ) : null}
          </Grid>
        </Box>

        <Box mb={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2">
                {t('search:documents-certificates')}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Grid container>
            <Grid item xs={12}>
              <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                <InputLabel htmlFor="documents" shrink>
                  {t('search:documents-certificates')}
                </InputLabel>
                <AutocompleteChips
                  id="documents"
                  className={`${
                    documentsAndCertificates ? classes.hasValue : ''
                  }`}
                  options={documentsAndCertificatesOptions}
                  placeholder={t('search:select-doc-certicates')}
                  value={documentsAndCertificates}
                  setValue={setDocumentsAndCertificates}
                  noOptionsText={t('search:no-options-doc-certicates')}
                />
              </form>
            </Grid>
          </Grid>
        </Box>

        <Box mb={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                <RadioGroup
                  aria-label="invalidDocuments"
                  name="invalidDocuments"
                  value={invalidDocuments}
                  onChange={handleInvalidDocumentsChange}
                >
                  <FormControlLabel
                    value="0"
                    control={<Radio />}
                    label={t('search:valid_documents')}
                  />
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label={t('search:invalid_documents')}
                  />
                </RadioGroup>
              </form>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </>
  )
}

AdvancedSearch.propTypes = {
  countries: PropTypes.array,
  provinces: PropTypes.array,
  documentsAndCertificates: PropTypes.array,
  especificDocuments: PropTypes.array,
}

export default AdvancedSearch
